import type { UrlLinkDecorationConfig } from '@/config/shared/url-context';

import {
  buildExcludeSelectors,
  buildExcludedDomains,
  buildSearchString,
  formatDecoratedHref,
  isBrowser,
  isDomainAllowed,
  parseSearchParams,
} from './utils';

const BLOCKED_PROTOCOLS = [
  'mailto:',
  'tel:',
  'javascript:',
  'data:',
  'blob:',
] as const;

export type LinkDecoratorOptions = {
  forwardedParams?: Readonly<Record<string, string>>;
  managedParams?: Readonly<Record<string, string>>;
  config: UrlLinkDecorationConfig;
  /**
   * Quando true, inclui `[data-no-sid]` nos exclude selectors.
   * Semântica legada: exclusão remove toda a decoração do link.
   */
  includeSidExclude?: boolean;
};

/**
 * Decora links com forwarded + managed params (grupos separados).
 *
 * Ordem:
 * 1. query existente do link
 * 2. forwarded (respeita preserveExisting / overwriteExisting)
 * 3. managed (sempre autoritativo)
 * 4. dedupe / remontagem
 *
 * Strategies: on_click | on_init | both.
 */
export class LinkDecorator {
  private readonly forwardedParams: Readonly<Record<string, string>>;

  private managedParams: Readonly<Record<string, string>>;

  private readonly config: UrlLinkDecorationConfig;

  private readonly excludedDomains: readonly string[];

  private readonly excludeSelectors: readonly string[];

  private clickHandler: ((event: MouseEvent) => void) | null = null;

  private started = false;

  constructor(
    paramsOrOptions: Record<string, string> | LinkDecoratorOptions,
    config?: UrlLinkDecorationConfig,
    includeSidExclude?: boolean
  ) {
    if (isLinkDecoratorOptions(paramsOrOptions)) {
      this.forwardedParams = Object.freeze({
        ...(paramsOrOptions.forwardedParams ?? {}),
      });
      this.managedParams = Object.freeze({
        ...(paramsOrOptions.managedParams ?? {}),
      });
      this.config = paramsOrOptions.config;
      this.excludedDomains = buildExcludedDomains(
        paramsOrOptions.config.excludedDomains,
        paramsOrOptions.config.mergeDefaultExcludedDomains
      );
      this.excludeSelectors = buildExcludeSelectors(
        paramsOrOptions.config.excludeSelectors,
        paramsOrOptions.config.mergeDefaultExcludeSelectors,
        paramsOrOptions.includeSidExclude === true
      );
      return;
    }

    // Legado: params flat tratados como managed (autoritativos).
    const flat = paramsOrOptions;
    const resolvedConfig = config!;
    this.forwardedParams = Object.freeze({});
    this.managedParams = Object.freeze({ ...flat });
    this.config = resolvedConfig;
    this.excludedDomains = buildExcludedDomains(
      resolvedConfig.excludedDomains,
      resolvedConfig.mergeDefaultExcludedDomains
    );
    this.excludeSelectors = buildExcludeSelectors(
      resolvedConfig.excludeSelectors,
      resolvedConfig.mergeDefaultExcludeSelectors,
      includeSidExclude === true
    );
  }

  /**
   * Atualiza managed params após o start (ex.: `oid` registrado no checkout).
   * Reaplica decoração on_init/both nos links já presentes.
   */
  updateManagedParams(managedParams: Readonly<Record<string, string>>): void {
    this.managedParams = Object.freeze({ ...managedParams });
    if (!this.started || !isBrowser()) return;
    if (this.config.enabled === false) return;
    const strategy = this.config.strategy ?? 'on_click';
    if (strategy === 'on_init' || strategy === 'both') {
      this.decorateMatchingLinks();
    }
  }

  start(): void {
    if (this.started || !isBrowser()) return;
    if (this.config.enabled === false) return;
    if (
      Object.keys(this.forwardedParams).length === 0 &&
      Object.keys(this.managedParams).length === 0
    ) {
      return;
    }

    this.started = true;
    const strategy = this.config.strategy ?? 'on_click';

    if (strategy === 'on_init' || strategy === 'both') {
      this.decorateMatchingLinks();
    }

    if (strategy === 'on_click' || strategy === 'both') {
      this.clickHandler = (event: MouseEvent) => {
        const anchor = findAnchor(event.target);
        if (!anchor) return;
        if (this.isExcludedAnchor(anchor)) return;
        this.decorateAnchor(anchor);
      };
      document.addEventListener('click', this.clickHandler, true);
    }
  }

  destroy(): void {
    if (!this.started) return;
    if (this.clickHandler && typeof document !== 'undefined') {
      document.removeEventListener('click', this.clickHandler, true);
    }
    this.clickHandler = null;
    this.started = false;
  }

  /** Expõe para testes e uso interno. */
  decorateHref(href: string, baseHostname?: string): string | null {
    if (!href || href.trim() === '') return null;

    const trimmed = href.trim();
    const lower = trimmed.toLowerCase();

    for (const proto of BLOCKED_PROTOCOLS) {
      if (!lower.startsWith(proto)) continue;
      if (proto === 'mailto:' && this.config.decorateMailto === true) {
        return null;
      }
      if (proto === 'tel:' && this.config.decorateTel === true) {
        return null;
      }
      return null;
    }

    if (trimmed.startsWith('#') && this.config.decorateHashLinks !== true) {
      return null;
    }

    let url: URL;
    try {
      const base = isBrowser()
        ? window.location.href
        : `https://${baseHostname ?? 'example.com'}/`;
      url = new URL(trimmed, base);
    } catch {
      return null;
    }

    if (
      !isDomainAllowed(
        url.hostname,
        this.config.allowedDomains,
        this.excludedDomains
      )
    ) {
      return null;
    }

    const existing = parseSearchParams(url.search);
    const preserve = this.config.preserveExisting !== false;
    const overwrite = this.config.overwriteExisting === true;
    const next: Record<string, string> = { ...existing };
    let changed = false;

    // 1) Forwarded — respeitam preserveExisting / overwriteExisting
    for (const [key, value] of Object.entries(this.forwardedParams)) {
      if (key in next && preserve && !overwrite) {
        continue;
      }
      if (next[key] !== value) changed = true;
      next[key] = value;
    }

    // 2) Managed — sempre autoritativos
    for (const [key, value] of Object.entries(this.managedParams)) {
      if (next[key] !== value) changed = true;
      next[key] = value;
    }

    const originalParsed = parseSearchParams(
      trimmed.includes('?')
        ? (trimmed.slice(trimmed.indexOf('?')).split('#')[0] ?? '')
        : ''
    );
    const hadDuplicateKeys =
      countQueryKeys(trimmed) > Object.keys(originalParsed).length;

    if (!changed && !hadDuplicateKeys) {
      return trimmed;
    }

    const search = buildSearchString(next);
    url.search = search.startsWith('?') ? search.slice(1) : search;
    return formatDecoratedHref(trimmed, url);
  }

  private decorateMatchingLinks(): void {
    // Sem selector: todas as âncoras; filtro real é allowedDomains em decorateHref
    const selector = this.config.selector ?? 'a[href]';
    let nodes: NodeListOf<Element>;
    try {
      nodes = document.querySelectorAll(selector);
    } catch {
      return;
    }
    for (const node of nodes) {
      if (!(node instanceof HTMLAnchorElement)) continue;
      if (this.isExcludedAnchor(node)) continue;
      this.decorateAnchor(node);
    }
  }

  private isExcludedAnchor(anchor: HTMLAnchorElement): boolean {
    if (anchor.hasAttribute('download')) return true;

    for (const sel of this.excludeSelectors) {
      try {
        if (anchor.matches(sel)) return true;
      } catch {
        // selector inválido — ignorar
      }
    }

    return false;
  }

  private decorateAnchor(anchor: HTMLAnchorElement): void {
    const href = anchor.getAttribute('href');
    if (!href) return;
    const next = this.decorateHref(href);
    if (next != null && next !== href) {
      anchor.setAttribute('href', next);
    }
  }
}

function isLinkDecoratorOptions(
  value: Record<string, string> | LinkDecoratorOptions
): value is LinkDecoratorOptions {
  return (
    typeof value === 'object' &&
    value !== null &&
    'config' in value &&
    typeof (value as LinkDecoratorOptions).config === 'object' &&
    (value as LinkDecoratorOptions).config !== null
  );
}

function findAnchor(target: EventTarget | null): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest('a[href]');
}

/** Conta chaves brutas na query (inclui duplicadas). */
function countQueryKeys(href: string): number {
  const qIndex = href.indexOf('?');
  if (qIndex < 0) return 0;
  const hashIndex = href.indexOf('#', qIndex);
  const raw =
    hashIndex >= 0 ? href.slice(qIndex + 1, hashIndex) : href.slice(qIndex + 1);
  if (!raw) return 0;
  return raw.split('&').filter(Boolean).length;
}
